import React, { useState, useEffect } from 'react';
import { Save, Loader2, AlertTriangle, FileSpreadsheet, History } from 'lucide-react';
import { PageTitle } from '../../../../_metronic/layout/core';
import { Modal } from 'react-bootstrap';
import { ConfirmationModal } from './ConfirmationModal';
import { EditableTable } from './EditableTable';
import { HistoryModal } from './HistoryModal';
import { TableData, Change } from './types';
import * as XLSX from 'xlsx';
import * as API from '../../../api';

const EMPTY_TABLE_DATA: TableData = {
  config: { columns: [] },
  rows: [],
};

type DataFormat = {
  [status: string]: {
    rows: any[][],
    config: {
      columns: { key: string, label: string, format?: string, locked?: boolean }[]
    }
  }
};

function transformData(data: DataFormat, year: string | number) {
  const result: any[] = [];

  Object.entries(data).forEach(([status, { rows, config }]) => {
    const monthLabels = config.columns.slice(2).map(col => col.label);

    rows.forEach(row => {
      const [BU, Industria, ...values] = row;

      monthLabels.forEach((mes, idx) => {
        result.push({
          Status: status,
          MesMeta: mes,
          AnoMeta: String(year),
          Industria,
          Valor: values[idx] !== "" && values[idx] != null ? Number(values[idx]) : null,
          BU
        });
      });
    });
  });

  return result;
}


export const DynamicTableEditor = () => {
  const [selectedYear, setSelectedYear] = useState('2026');
  const [approvedData, setApprovedData] = useState<TableData>(EMPTY_TABLE_DATA);
  const [billedData, setBilledData] = useState<TableData>(EMPTY_TABLE_DATA);
  const [editedCells, setEditedCells] = useState<Map<string, string>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const getData = async() => {
    const response = await API.getDataTransferMetaFinanceira(selectedYear);
    const responseData = response.data.data;
    const json = JSON.parse(responseData.data);
    
    setApprovedData(json.approved);
    setBilledData(json.billed);
    setEditedCells(new Map());
  }

  useEffect(() => {
    getData();
  }, [selectedYear]);

  const handleCellChange = (rowIndex: number, colIndex: number, value: string, isApproved: boolean) => {
    const data = isApproved ? approvedData : billedData;
    
    // Check if cell is in a locked row or column
    if (data.config.lockedRows?.includes(rowIndex)) return;
    if (data.config.columns[colIndex].locked) return;

    const cellKey = `${isApproved ? 'approved' : 'billed'}-${rowIndex}-${colIndex}`;
    const originalValue = data.rows[rowIndex][colIndex];

    if (value === originalValue) {
      const newEdited = new Map(editedCells);
      newEdited.delete(cellKey);
      setEditedCells(newEdited);
    } else {
      setEditedCells(new Map(editedCells).set(cellKey, value));
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const updatedApprovedRows = approvedData.rows.map((row:any, rowIndex:any) => 
        row.map((cell:any, colIndex:any) => {
          const cellKey = `approved-${rowIndex}-${colIndex}`;
          return editedCells.has(cellKey) ? editedCells.get(cellKey)! : cell.replaceAll(',', '.');
        })
      );

      const updatedBilledRows = billedData.rows.map((row:any, rowIndex:any) => 
        row.map((cell:any, colIndex:any) => {
          const cellKey = `billed-${rowIndex}-${colIndex}`;
          return editedCells.has(cellKey) ? editedCells.get(cellKey)! : cell.replaceAll(',', '.');
        })
      );
      const extractData = transformData({
        approved:{ ...approvedData, rows: updatedApprovedRows }, 
        billed:{ ...billedData, rows: updatedBilledRows }
      }, selectedYear);
      const response = await API.updateDataTransferMetaFinanceira(
        {
          selectedYear,
          changes: {
            approved: approvedData, 
            billed: billedData
          },
          table: {
            approved:{ ...approvedData, rows: updatedApprovedRows }, 
            billed:{ ...billedData, rows: updatedBilledRows },
            extract:extractData
          },
          extract:extractData
        }, selectedYear);

      setApprovedData({ ...approvedData, rows: updatedApprovedRows });
      setBilledData({ ...billedData, rows: updatedBilledRows });
      setEditedCells(new Map());
      setShowConfirmModal(false);
    } catch (error) {
      console.error('Error saving changes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getChangeSummary = (): Change[] => {
    const changes: Change[] = [];
    
    editedCells.forEach((newValue, key) => {
      const [table, rowIndex, colIndex] = key.split('-');
      const data = table === 'approved' ? approvedData : billedData;
      const originalValue = data.rows[Number(rowIndex)][Number(colIndex)];
      const rowName = data.rows[Number(rowIndex)][0];
      const colName = data.config.columns[Number(colIndex)].label;
      
      changes.push({
        cell: `${table === 'approved' ? 'APROVADO' : 'FATURADO'} - ${rowName} - ${colName}`,
        original: originalValue,
        new: newValue
      });
    });

    return changes;
  };

  const handleExport = () => {
    const wb = XLSX.utils.book_new();

    const approvedWS = XLSX.utils.aoa_to_sheet([
      approvedData.config.columns.map((col:any) => col.label),
      ...approvedData.rows
    ]);

    const billedWS = XLSX.utils.aoa_to_sheet([
      billedData.config.columns.map((col:any) => col.label),
      ...billedData.rows
    ]);

    XLSX.utils.book_append_sheet(wb, approvedWS, "Aprovado");
    XLSX.utils.book_append_sheet(wb, billedWS, "Faturado");

    XLSX.writeFile(wb, `tabela-dinamica-${selectedYear}.xlsx`);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="mx-auto px-2 sm:px-3 lg:px-4">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <PageTitle>Data Transfer - Meta Financeira</PageTitle>
            <div className="flex items-center gap-4">
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="px-4 py-2 rounded-lg border border-gray-200 bg-white focus:ring-2 focus:ring-blue-500"
              >
                <option value="2023">2023</option>
                <option value="2024">2024</option>
                <option value="2025">2025</option>
                <option value="2026">2026</option>
              </select>
              <button
                onClick={() => setShowHistoryModal(true)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors inline-flex items-center gap-2"
              >
                <History className="w-5 h-5" />
                Histórico
              </button>
              <button
                onClick={handleExport}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors inline-flex items-center gap-2"
              >
                <FileSpreadsheet className="w-5 h-5" />
                Exportar Excel
              </button>
              <button
                onClick={() => setShowConfirmModal(true)}
                disabled={editedCells.size === 0 || isLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Salvando...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    <span>Salvar Alterações</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {editedCells.size > 0 && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
              <div className="flex items-center gap-2 text-yellow-800">
                <AlertTriangle className="w-5 h-5" />
                <p className="text-sm font-medium">
                  {editedCells.size} célula(s) com alterações pendentes
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Tables */}
        <div className="space-y-8">
          {/* Approved Table */}
          <div>
            <div className="bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-3 rounded-t-xl">
              <h2 className="text-lg font-semibold text-white">APROVADO</h2>
            </div>
            <div className="bg-white rounded-b-xl shadow-sm border-x border-b border-gray-200 overflow-hidden">
              <EditableTable
                data={approvedData}
                editedCells={editedCells}
                onCellChange={(rowIndex, colIndex, value) => handleCellChange(rowIndex, colIndex, value, true)}
                tablePrefix="approved"
                compact
              />
            </div>
          </div>

          {/* Billed Table */}
          <div>
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-t-xl">
              <h2 className="text-lg font-semibold text-white">FATURADO</h2>
            </div>
            <div className="bg-white rounded-b-xl shadow-sm border-x border-b border-gray-200 overflow-hidden">
              <EditableTable
                data={billedData}
                editedCells={editedCells}
                onCellChange={(rowIndex, colIndex, value) => handleCellChange(rowIndex, colIndex, value, false)}
                tablePrefix="billed"
                compact
              />
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <Modal 
        show={showConfirmModal} 
        onHide={() => setShowConfirmModal(false)}
        centered
      >
        <ConfirmationModal
          changes={getChangeSummary()}
          onConfirm={handleSave}
          onCancel={() => setShowConfirmModal(false)}
          isLoading={isLoading}
          config={approvedData?.config}
        />
      </Modal>

      <Modal
        show={showHistoryModal}
        onHide={() => setShowHistoryModal(false)}
        dialogClassName="modal-xl"
        centered
      >
        <HistoryModal
          onClose={() => setShowHistoryModal(false)}
          year={selectedYear}
        />
      </Modal>
    </div>
  );
};

export default DynamicTableEditor;